import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { onAuthStateChange, refreshUserToken, checkWhitelistStatus, isUserAdmin, isUserEditor, isUserSdr } from './firebase/auth'
import Login from './components/Login'
import PermissionsPending from './components/PermissionsPending'
import Dashboard from './components/Dashboard'
import Contacts from './components/Contacts'
import ContactDetail from './components/ContactDetail'
import Flows from './components/Flows'
import Deals from './components/Deals'
import TagManager from './components/TagManager'
import Analytics from './components/Analytics'
import BANTRef from './components/BANTRef'
import ICPHub from './components/ICPHub'
import PricingPlaybook from './components/PricingPlaybook'
import PriceList from './components/PriceList'
import AISecMarkets from './components/AISecMarkets'
import AISecScripts from './components/AISecScripts'
import EnquiryResponseChecklist from './components/EnquiryResponseChecklist'
import Files from './components/Files'
import ProspectResearch from './components/ProspectResearch'
import CompanyProspectResearch from './components/CompanyProspectResearch'
import JobRoleAnalysis from './components/JobRoleAnalysis'
import Admin from './components/Admin'
import WebsiteArticles from './components/WebsiteArticles'
import DevSecOpsWebsiteArticles from './components/DevSecOpsWebsiteArticles'
import LinkedInPosts from './components/LinkedInPosts'
import LinkedInCallback from './components/LinkedInCallback'
import AdImageCreator from './components/AdImageCreator'
import EditorsNote from './components/EditorsNote'
import MyNotes from './components/MyNotes'
import Investment from './components/Investment'
import './App.css'

const ADMIN_ONLY_PATHS = ['/admin', '/investment']
const EDITOR_OR_ADMIN_PATHS = ['/website-articles', '/devsecops-website-articles', '/linkedin-posts', '/ad-image-creator', '/editors-note']

/** Paths SDR-only users may open (custom claim sdr:true, not admin/editor). */
const SDR_ALLOWED_PATHS = [
  '/deals',
  '/files',
  '/bant-ref',
  '/icp-hub',
  '/pricing-playbook',
  '/price-list',
  '/aisec-markets',
  '/aisec-scripts',
  '/enquiry-checklist',
  '/prospect-research',
  '/company-prospect-research',
  '/notes'
]

function hasAnyRole({ isAdmin, isEditor, isSdr }) {
  return isAdmin || isEditor || isSdr
}

function isSdrOnly({ isAdmin, isEditor, isSdr }) {
  return isSdr && !isAdmin && !isEditor
}

function pathAllowedForSdr(path) {
  return SDR_ALLOWED_PATHS.some(p => path === p || path.startsWith(p + '/'))
}

function accessFallback(claims) {
  return isSdrOnly(claims) ? '/deals' : '/contacts'
}

function canAccess(path, claims) {
  const { isAdmin, isEditor, isSdr } = claims
  if (ADMIN_ONLY_PATHS.some(p => path === p || path.startsWith(p + '/'))) return isAdmin
  if (EDITOR_OR_ADMIN_PATHS.some(p => path === p || path.startsWith(p + '/'))) return isEditor || isAdmin
  if (path === '/analytics' || path.startsWith('/analytics/') || path === '/tags' || path.startsWith('/tags/')) return isAdmin || isEditor || !isSdr
  if (isSdrOnly(claims)) return pathAllowedForSdr(path)
  return true
}

function App() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [claims, setClaims] = useState({ isAdmin: false, isEditor: false, isSdr: false })

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (authUser) => {
      if (authUser) {
        try {
          await refreshUserToken(authUser)
          try {
            await checkWhitelistStatus()
            const [isAdmin, isEditor, isSdr] = await Promise.all([
              isUserAdmin(authUser, true),
              isUserEditor(authUser, true),
              isUserSdr(authUser, true)
            ])
            setUser(authUser)
            setClaims({ isAdmin, isEditor, isSdr })
          } catch (error) {
            console.error('Whitelist check failed:', error)
            setUser(null)
            setClaims({ isAdmin: false, isEditor: false, isSdr: false })
          }
        } catch (error) {
          console.error('Token refresh failed:', error)
          if (error.code === 'auth/user-not-found' || error.code === 'auth/user-disabled' || error.code === 'auth/internal-error') {
            setUser(null)
            setClaims({ isAdmin: false, isEditor: false, isSdr: false })
          } else {
            setUser(authUser)
            const [isAdmin, isEditor, isSdr] = await Promise.all([
              isUserAdmin(authUser, true).catch(() => false),
              isUserEditor(authUser, true).catch(() => false),
              isUserSdr(authUser, true).catch(() => false)
            ])
            setClaims({ isAdmin, isEditor, isSdr })
          }
        }
      } else {
        setUser(null)
        setClaims({ isAdmin: false, isEditor: false, isSdr: false })
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    )
  }

  if (user && !hasAnyRole(claims)) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<PermissionsPending user={user} />} />
        </Routes>
      </Router>
    )
  }

  return (
    <Router>
      <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <Login />}
          />
          <Route
            path="/"
            element={user ? <Dashboard user={user} claims={claims} /> : <Navigate to="/login" replace />}
          >
            <Route index element={<Navigate to={accessFallback(claims)} replace />} />
            <Route path="contacts" element={canAccess('/contacts', claims) ? <Contacts user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="contacts/:id" element={canAccess('/contacts', claims) ? <ContactDetail user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="flows" element={canAccess('/flows', claims) ? <Flows user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="deals" element={canAccess('/deals', claims) ? <Deals user={user} isAdmin={claims.isAdmin} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="tags" element={canAccess('/tags', claims) ? <TagManager user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="analytics" element={canAccess('/analytics', claims) ? <Analytics user={user} isAdmin={claims.isAdmin} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="bant-ref" element={canAccess('/bant-ref', claims) ? <BANTRef /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="icp-hub" element={canAccess('/icp-hub', claims) ? <ICPHub /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="pricing-playbook" element={canAccess('/pricing-playbook', claims) ? <PricingPlaybook /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="price-list" element={canAccess('/price-list', claims) ? <PriceList /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="aisec-markets" element={canAccess('/aisec-markets', claims) ? <AISecMarkets /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="aisec-scripts" element={canAccess('/aisec-scripts', claims) ? <AISecScripts /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="enquiry-checklist" element={canAccess('/enquiry-checklist', claims) ? <EnquiryResponseChecklist /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="files" element={canAccess('/files', claims) ? <Files user={user} filesReadOnly={isSdrOnly(claims)} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="notes" element={canAccess('/notes', claims) ? <MyNotes user={user} isAdmin={claims.isAdmin} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="prospect-research" element={canAccess('/prospect-research', claims) ? <ProspectResearch user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="company-prospect-research" element={canAccess('/company-prospect-research', claims) ? <CompanyProspectResearch user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="job-role-analysis" element={canAccess('/job-role-analysis', claims) ? <JobRoleAnalysis user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="admin" element={canAccess('/admin', claims) ? <Admin user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="investment" element={canAccess('/investment', claims) ? <Investment user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="website-articles" element={canAccess('/website-articles', claims) ? <WebsiteArticles user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="devsecops-website-articles" element={canAccess('/devsecops-website-articles', claims) ? <DevSecOpsWebsiteArticles user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="linkedin-posts" element={canAccess('/linkedin-posts', claims) ? <LinkedInPosts user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="linkedin-posts/callback" element={<LinkedInCallback />} />
          <Route path="ad-image-creator" element={canAccess('/ad-image-creator', claims) ? <AdImageCreator /> : <Navigate to={accessFallback(claims)} replace />} />
          <Route path="editors-note" element={canAccess('/editors-note', claims) ? <EditorsNote user={user} /> : <Navigate to={accessFallback(claims)} replace />} />
        </Route>
      </Routes>
    </Router>
  )
}

export default App

